import {
  FanEventFragment,
  GetFanEngagementDocument,
  GetFanEngagementQuery,
  GetStageEngagementDocument,
  GetStageEngagementQuery,
  StageEventFragment,
  useGetFanEngagementQuery,
  useGetStageEngagementQuery,
  useOnEngagementChangedSubscription,
  useOnMockEngagementChangedSubscription,
} from '@/generated/graphql';
import { keyBy } from '@/utils/keyBy';
import { useEffect, useMemo, useReducer } from 'react';
import {
  screenThemeStateContext,
  defaultInitialState,
} from './ScreenThemeStateContext';
import { screenThemeStateReducer } from './reducer';
import { setActiveEngagement } from '../sharedActions';
import { EngagementMode } from '@/types/screen';
import { useSearchParams } from 'react-router-dom';
import { createLogger } from '@/utils/createLogger';
import { createMutableCopy } from '@/utils/createMutableCopy';

const logger = createLogger('ScreenThemeStateProvider');

type Props = {
  children: React.ReactNode;
  engagementMode?: EngagementMode;
  setEngagementMode?: (mode: EngagementMode) => void;
} & (
  | { event: StageEventFragment; screenType: 'stage' }
  | { event: FanEventFragment; screenType: 'fan' }
);

export function ScreenThemeStateProvider({
  children,
  event,
  screenType,
  engagementMode,
  setEngagementMode,
}: Props) {
  const [searchParams] = useSearchParams();
  const engagementIdOverride = searchParams.get('engagementId');

  // Always call both hooks with skip parameter
  const { data: stageEngagementResult } = useGetStageEngagementQuery({
    skip: !engagementIdOverride || screenType !== 'stage',
    variables: { engagementId: engagementIdOverride || '' },
  });

  const { data: fanEngagementResult } = useGetFanEngagementQuery({
    skip: !engagementIdOverride || screenType !== 'fan',
    variables: { engagementId: engagementIdOverride || '' },
  });

  useOnEngagementChangedSubscription({
    skip: !engagementIdOverride,
    variables: { engagementId: engagementIdOverride || '' },
    onData: ({ client, data }) => {
      const changes = data?.data?.engagementChanged?.engagement;
      if (!changes || !engagementIdOverride) {
        return;
      }
      if (screenType === 'stage') {
        client.cache.updateQuery(
          {
            query: GetStageEngagementDocument,
            variables: { engagementId: engagementIdOverride },
          },
          (cachedData: GetStageEngagementQuery | null) => {
            if (!cachedData?.engagement) return cachedData;
            return {
              engagement: {
                ...cachedData.engagement,
                ...changes,
              },
            } as GetStageEngagementQuery;
          },
        );
      } else {
        client.cache.updateQuery(
          {
            query: GetFanEngagementDocument,
            variables: { engagementId: engagementIdOverride },
          },
          (cachedData: GetFanEngagementQuery | null) => {
            if (!cachedData?.engagement) return cachedData;
            return {
              engagement: {
                ...cachedData.engagement,
                ...changes,
              },
            } as GetFanEngagementQuery;
          },
        );
      }
    },
  });
  useOnMockEngagementChangedSubscription({
    skip: !engagementIdOverride,
    variables: { engagementId: engagementIdOverride || '' },
    onData: ({ client, data }) => {
      const changes = data?.data?.mockEngagementChanged?.engagement;
      if (!changes || !engagementIdOverride) {
        return;
      }
      if (screenType === 'stage') {
        client.cache.updateQuery(
          {
            query: GetStageEngagementDocument,
            variables: { engagementId: engagementIdOverride },
          },
          (cachedData: GetStageEngagementQuery | null) => {
            if (!cachedData?.engagement) return cachedData;
            return {
              engagement: {
                ...cachedData.engagement,
                ...changes,
              },
            } as GetStageEngagementQuery;
          },
        );
      } else {
        client.cache.updateQuery(
          {
            query: GetFanEngagementDocument,
            variables: { engagementId: engagementIdOverride },
          },
          (cachedData: GetFanEngagementQuery | null) => {
            if (!cachedData?.engagement) return cachedData;
            return {
              engagement: {
                ...cachedData.engagement,
                ...changes,
              },
            } as GetFanEngagementQuery;
          },
        );
      }
    },
  });

  // Choose the appropriate result based on screen type
  const overrideEngagementResult =
    screenType === 'stage' ? stageEngagementResult : fanEngagementResult;

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      engagementMode: engagementMode || EngagementMode.Actual,
      savedTheme: getInitialSavedTheme(event),
      eventId: event.id,
    }),
    [event, engagementMode],
  );

  const [state, dispatch] = useReducer(screenThemeStateReducer, initialState);

  useEffect(() => {
    if (!engagementIdOverride) {
      logger.info(`event.activeEngagement`, event.activeEngagement);
      dispatch(setActiveEngagement({ engagement: event.activeEngagement }));
    } else if (overrideEngagementResult?.engagement) {
      logger.info(
        `overrideEngagementResult.engagement`,
        overrideEngagementResult.engagement,
      );
      dispatch(
        setActiveEngagement({
          engagement: overrideEngagementResult.engagement,
        }),
      );
    }
  }, [
    event.activeEngagement,
    engagementIdOverride,
    overrideEngagementResult?.engagement,
  ]);

  useEffect(() => {
    setEngagementMode?.(state.engagementMode);
  }, [setEngagementMode, state.engagementMode]);

  return (
    <screenThemeStateContext.Provider value={{ state, dispatch }}>
      {children}
    </screenThemeStateContext.Provider>
  );
}

function getInitialSavedTheme(event: StageEventFragment | FanEventFragment) {
  const themeConfig = event.themeConfig || {};
  return {
    ...themeConfig,
    stageElements: keyBy(
      (themeConfig.stageElements || []).map((element) =>
        createMutableCopy(element),
      ),
      'id',
    ),
    fanElements: keyBy(
      (themeConfig.fanElements || []).map((element) =>
        createMutableCopy(element),
      ),
      'id',
    ),
  };
}
